<script setup lang="ts">
import { computed, ref, watch } from "vue";
import { bemBuilder, toMoney } from "@chatfood/core-utils";
import { SatButton, AtomText, useDate } from "@chatfood/design-system";
import type { IDomainPastOrder } from "~/domain/past-order";
import { useCustomerStore } from "~/stores/customer";
import { useFeatureMatrixStore } from "~/stores/featureMatrix";
import { useLocale } from "~/composables/use-locale";
import { useDomainLabel } from "~/composables/use-domain-label";
import { useDirectBackend } from "#imports";

const css = bemBuilder("past-orders");

const props = defineProps<{ hasBorder?: boolean }>();

const customer = useCustomerStore();
const featureMatrix = useFeatureMatrixStore();

const { t } = useLocale();
const { customerRepo } = useDirectBackend();
const { formatDatetime } = useDate();
const { orderingModeLabel } = useDomainLabel();

const orders = ref<IDomainPastOrder[]>([]);

const shouldFetchOrders = computed(() => {
  return customer.isLogged && featureMatrix.isToggleEnabled("enable-reorder");
});

const canShowSection = computed(
  () => shouldFetchOrders.value && orders.value.length > 0,
);

async function loadPastOrders() {
  try {
    const response = await customerRepo.getPastOrders({
      perPage: 5,
      page: 1,
    });

    orders.value = response.data;
  } catch {
    orders.value = [];
  }
}

function getOrderItems(items: IDomainPastOrder["items"]): string {
  return items
    .map((s) => {
      const prefix = s.quantity > 1 ? `${s.quantity}x ` : "";
      return `${prefix}${s.name}`;
    })
    .join(", ");
}

function handleReorder(orderId: string) {
  alert(`Reorder clicked for order ID: ${orderId}`);
}

watch(
  () => shouldFetchOrders.value,
  (value) => {
    if (value) loadPastOrders();
  },
  { immediate: true },
);
</script>

<template>
  <section v-if="canShowSection" :class="css('')">
    <slot />
    <ul :class="css('container')">
      <li
        v-for="order in orders"
        :key="order.id"
        :class="[css('card'), { [css('card', 'border')]: props.hasBorder }]"
      >
        <AtomText type="body-bold">
          {{ formatDatetime(order.orderDate, "DD MMMM YYYY, hh:mma") }}
        </AtomText>
        <div>
          <AtomText type="small" color="color-text-secondary">
            {{
              t("components.past_orders.order_label", {
                orderId: order.orderDisplayId,
                total: toMoney(order.payment.value, order.payment.currency),
                orderingMode: orderingModeLabel(order.orderType),
              })
            }}
          </AtomText>
          <AtomText
            type="small"
            color="color-text-secondary"
            :class="css('card-items')"
          >
            {{ getOrderItems(order.items) }}
          </AtomText>
        </div>
        <SatButton
          size="small"
          theme="ghost"
          variant="primary"
          @click="handleReorder(order.id)"
        >
          {{ t("components.past_orders.reorder") }}
        </SatButton>
      </li>
    </ul>
  </section>
</template>

<style lang="scss" scoped>
.past-orders {
  display: flex;
  gap: var(--spacing-sm);
  flex-direction: column;

  &__container {
    display: flex;
    overflow-x: auto;
    gap: var(--spacing-sm);
    margin: 0;
    padding: 0 0 var(--spacing-md) 0;
  }

  &__card {
    display: flex;
    min-width: 280px;
    flex-direction: column;
    align-items: flex-start;
    gap: var(--spacing-sm);
    padding: var(--spacing-md);
    border-radius: var(--spacing-md);
    background-color: var(--color-base-100);

    &--border {
      border: 1px solid var(--color-base-80);
    }

    @media (width > $breakpoint-sm) {
      min-width: 45%;
    }

    &:first-child {
      margin-left: var(--spacing-lg);
    }

    &:last-child {
      margin-right: var(--spacing-lg);
    }
  }

  &__card-items {
    overflow: hidden;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    line-clamp: 2;
    -webkit-box-orient: vertical;
  }
}
</style>
